const PAGE_WIDTH = 595;
const PAGE_HEIGHT = 842;
const MARGIN = 48;

const ascii = (value) => String(value ?? "")
  .normalize("NFKD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[–—]/g, "-")
  .replace(/[^\x20-\x7E]/g, " ")
  .replace(/\\/g, "\\\\")
  .replace(/\(/g, "\\(")
  .replace(/\)/g, "\\)");

const wrap = (text, size) => {
  const maxChars = Math.max(24, Math.floor((PAGE_WIDTH - MARGIN * 2) / (size * 0.52)));
  const words = ascii(text).split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let current = "";
  words.forEach((word) => {
    if (!current) current = word;
    else if (`${current} ${word}`.length <= maxChars) current += ` ${word}`;
    else {
      lines.push(current);
      current = word;
    }
  });
  if (current) lines.push(current);
  return lines;
};

const layoutPages = (entries) => {
  const pages = [[]];
  let y = PAGE_HEIGHT - MARGIN;
  const addPage = () => {
    pages.push([]);
    y = PAGE_HEIGHT - MARGIN;
  };

  entries.forEach((raw) => {
    const entry = typeof raw === "string" ? { text: raw } : raw || {};
    if (entry.pageBreak) {
      addPage();
      return;
    }
    if (entry.rule) {
      if (y < MARGIN + 24) addPage();
      pages[pages.length - 1].push({ rule: true, y });
      y -= entry.gap || 18;
      return;
    }
    if (entry.text === "" || entry.spacer) {
      y -= entry.gap || 12;
      if (y < MARGIN) addPage();
      return;
    }

    const size = entry.size || 10;
    const lineHeight = entry.lineHeight || size + 5;
    const wrapped = wrap(entry.text, size);
    wrapped.forEach((line) => {
      if (y < MARGIN + lineHeight) addPage();
      pages[pages.length - 1].push({ ...entry, text: line, size, y });
      y -= lineHeight;
    });
    y -= entry.gap || 0;
  });
  return pages;
};

const streamForPage = (entries) => entries.map((entry) => {
  if (entry.rule) return `0.72 G 0.8 w ${MARGIN} ${entry.y} m ${PAGE_WIDTH - MARGIN} ${entry.y} l S`;
  const size = entry.size || 10;
  const estimatedWidth = ascii(entry.text).length * size * 0.5;
  const x = entry.align === "center"
    ? Math.max(MARGIN, (PAGE_WIDTH - estimatedWidth) / 2)
    : entry.align === "right"
      ? Math.max(MARGIN, PAGE_WIDTH - MARGIN - estimatedWidth)
      : MARGIN + (entry.indent || 0);
  const color = entry.muted ? "0.35 g" : "0 g";
  return `BT ${color} /${entry.bold ? "F2" : "F1"} ${size} Tf 1 0 0 1 ${x.toFixed(1)} ${entry.y.toFixed(1)} Tm (${ascii(entry.text)}) Tj ET`;
}).join("\n");

export const createPdfBlob = (entries = []) => {
  const pages = layoutPages(entries);
  const objects = [];
  objects[1] = "<< /Type /Catalog /Pages 2 0 R >>";
  objects[3] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>";
  objects[4] = "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>";

  const kids = [];
  pages.forEach((page, index) => {
    const pageId = 5 + index * 2;
    const contentId = pageId + 1;
    const stream = streamForPage(page);
    kids.push(`${pageId} 0 R`);
    objects[pageId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] /Resources << /Font << /F1 3 0 R /F2 4 0 R >> >> /Contents ${contentId} 0 R >>`;
    objects[contentId] = `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`;
  });
  objects[2] = `<< /Type /Pages /Kids [${kids.join(" ")}] /Count ${pages.length} >>`;

  let pdf = "%PDF-1.4\n";
  const offsets = [0];
  for (let id = 1; id < objects.length; id += 1) {
    offsets[id] = pdf.length;
    pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
  }
  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length}\n0000000000 65535 f \n`;
  for (let id = 1; id < objects.length; id += 1) {
    pdf += `${String(offsets[id]).padStart(10, "0")} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${objects.length} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`;
  return new Blob([pdf], { type: "application/pdf" });
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};
